/**
 * Client Configuration System
 *
 * This file defines the client-specific configuration that is stored in the database
 * and synced on user login. Each client (fulfillment center) can have custom settings.
 *
 * ## Modifying Fields
 *
 * When changing a field to ClientConfig:
 * 1. Add the field to the `ClientConfig` type with appropriate type
 * 2. Add the default value to `defaultClientConfig`
 * 3. INCREMENT `configVersion` - This triggers existing clients to update their DB config
 *
 * ## How Version Sync Works
 *
 * The `useClientConfigSync` hook (called in App.tsx) compares the local
 * `configVersion` with the server's version. If local > server, it updates the server
 * with the new shape. This ensures all new fields propagate to existing clients.
 *
 */

import { BoardLayout } from "~/features/andon/andon.types";
import { LanguageCode } from "~/localization_i18n";

export type Inv_InventoryDateLabelSettings = "manufacture" | "expiration";
export type Ap_totesPlacement = "right" | "split";

type as_homepageTypes = [
  "picking",
  "induction",
  "induction v2",
  "inventory holds",
  "cycle counts",
  "cycle counts v2",
  "inventory"
];

export type AS_HomepageType = as_homepageTypes[number];

const defaultHomepageTypes: AS_HomepageType[] = [
  "picking",
  "induction v2",
  "inventory holds",
  "cycle counts v2",
  "inventory"
];

export type ClientConfig = {
  configVersion: number;

  // andon
  an_andonFeaturesEnabled: boolean;
  an_boardLayouts: BoardLayout[];
  an_isHandRaiseTypePickerEnabled: boolean;

  // autostore picking
  ap_totesPlacement: Ap_totesPlacement;
  ap_prepTotesModalEnabled: boolean;
  ap_confirmCartNumberEnabled: boolean;
  ap_fusionPortScreenEnabled: boolean;
  ap_prepTotesModalShowTitle: boolean;
  ap_prepTotesModalShowInstructionText: boolean;
  ap_prepTotesModalColumnPlacement: boolean;
  ap_prepTotesModalShowToteId: boolean;
  ap_prepTotesModalShowCustomerName: boolean;
  ap_prepTotesModalShowToteSize: boolean;
  ap_scanningIndicatorShown: boolean;
  ap_addToteFunctionalityEnabled: boolean;
  ap_repickToteFunctionalityEnabled: boolean;
  ap_excludeRecalledHold: boolean;
  ap_includeDirtyBinHold: boolean;
  ap_includeDamagedBinHold: boolean;
  ap_includeMisconfiguredBinHold: boolean;
  ap_applyingLabelProcessEnabled: boolean;
  ap_showPickQtyWhileBinLoaderIsActive: boolean;
  ap_isTopAreaOfToteClickable: boolean;
  ap_showOrderOrProductCancellationModal: boolean;
  ap_binRemovedFlow: boolean;

  // batches
  bat_pickFromBatchesEnabled: boolean;
  bat_stageFromBatchesEnabled: boolean;

  // cleaningBin
  binCleaningEnabled: boolean;

  // dev
  dev_orderCreation: boolean;
  dev_useV2OrderCreation: boolean;
  dev_ptlSimulation: boolean;
  dev_conveyanceSimulation: boolean;

  // inventory
  inv_uomOptions: { value: string; label: string }[];
  inv_addSetExpirationEnabled: boolean;
  inv_addUomSelectionForASBinsDisabled: boolean;
  inv_addUseUomFromFC: boolean;
  inv_inventoryDateLabel: Inv_InventoryDateLabelSettings;
  inv_inventoryDateRequired: boolean;

  // operations (switch to op_variableName)
  manualOpsEnabled?: boolean;
  cartAssignmentEnabled: boolean;
  cartPrepEnabled: boolean;

  // orders
  ord_includeSpokes: boolean;

  // putaway
  putaway_multipleSearchTerms: boolean;
  putaway_showScanFilters: boolean;
  putaway_showExceptionsButton: boolean;

  // workstations
  ws_addRemovePortsEnabled: boolean;
  ws_modifyPortSetupEnabled: boolean;
  ws_deleteWorkstationEnabled: boolean;

  site_defaultLanguageCode: LanguageCode;

  // fc client homepage settings
  fc_autostore_homepages: AS_HomepageType[];
};

export const defaultClientConfig: ClientConfig = {
  configVersion: 1,
  inv_uomOptions: [{ value: "ea", label: "ea" }],
  an_andonFeaturesEnabled: true,
  an_boardLayouts: [],
  an_isHandRaiseTypePickerEnabled: false,

  ap_totesPlacement: "split" as Ap_totesPlacement,
  ap_prepTotesModalEnabled: true,
  ap_confirmCartNumberEnabled: false,
  ap_excludeRecalledHold: true,
  ap_includeDamagedBinHold: true,
  ap_includeDirtyBinHold: true,
  ap_includeMisconfiguredBinHold: true,
  ap_fusionPortScreenEnabled: true,
  ap_prepTotesModalShowTitle: true,
  ap_prepTotesModalShowInstructionText: false,
  ap_prepTotesModalColumnPlacement: true,
  ap_prepTotesModalShowToteId: false,
  ap_prepTotesModalShowCustomerName: false,
  ap_prepTotesModalShowToteSize: false,
  ap_scanningIndicatorShown: true,
  ap_addToteFunctionalityEnabled: true,
  ap_repickToteFunctionalityEnabled: true,
  ap_applyingLabelProcessEnabled: true,
  ap_showPickQtyWhileBinLoaderIsActive: true,
  ap_isTopAreaOfToteClickable: false,
  ap_showOrderOrProductCancellationModal: true,
  ap_binRemovedFlow: true,

  bat_pickFromBatchesEnabled: true,
  bat_stageFromBatchesEnabled: false,

  dev_orderCreation: false,
  dev_useV2OrderCreation: false,
  dev_ptlSimulation: false,
  dev_conveyanceSimulation: false,

  inv_addSetExpirationEnabled: true,
  inv_addUomSelectionForASBinsDisabled: true,
  inv_addUseUomFromFC: false,
  inv_inventoryDateLabel: "expiration" as Inv_InventoryDateLabelSettings,
  inv_inventoryDateRequired: false,

  manualOpsEnabled: false,
  cartAssignmentEnabled: false,
  cartPrepEnabled: false,
  binCleaningEnabled: true,

  ord_includeSpokes: true,

  putaway_multipleSearchTerms: true,
  putaway_showScanFilters: true,
  putaway_showExceptionsButton: true,

  ws_addRemovePortsEnabled: false,
  ws_modifyPortSetupEnabled: true,
  ws_deleteWorkstationEnabled: true,

  site_defaultLanguageCode: "bs",

  fc_autostore_homepages: defaultHomepageTypes
};
